import XDatePicker from '@/Mantine/XDatePicker'
import XNationalInput from '@/Mantine/XNationalInput'
import XPhoneInput from '@/Mantine/XPhoneInput'
import XSelect from '@/Mantine/XSelect'
import XTextInput from '@/Mantine/XTextInput'
import { RootState } from '@/Store'
import { updateCurrentStep, updateLead, updateLeadId } from '@/Store/leadFormSlice'
import { Button, Divider, HStack, TabItem, Tabs, VStack, XelaColor } from '@/XelaReact'
import { LoadingOverlay, SelectItem } from '@mantine/core'
import axios from 'axios'
import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const LeadFormPersonalDetail: FC<{
  fields: string[]
  requirements: string[]
  agencyId: number
  csrfToken: string
}> = ({ fields, requirements, agencyId, csrfToken }) => {
  const dispatch = useDispatch()
  const leadStore = useSelector((state: RootState) => state.leadForm)
  const [tab, setTab] = React.useState('Personal Details')
  const [loading, setLoading] = useState(false)
  const [countries, setCountries] = useState<SelectItem[]>([])
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const tabItems: TabItem[] = []

  if (
    fields.includes('given_name') ||
    fields.includes('surname') ||
    fields.includes('email') ||
    fields.includes('phone') ||
    fields.includes('national_id') ||
    fields.includes('gender') ||
    fields.includes('date_of_birth') ||
    fields.includes('martial_status') ||
    fields.includes('visa_type') ||
    fields.includes('referral')
  ) {
    tabItems.push({
      label: 'Personal Details',
      value: 'Personal Details',
    })
  }
  if (fields.includes('father_name') || fields.includes('mother_name')) {
    tabItems.push({
      label: 'Family Details',
      value: 'Family Details',
    })
  }
  if (
    fields.includes('address') ||
    fields.includes('city') ||
    fields.includes('pin_code') ||
    fields.includes('state') ||
    fields.includes('country_code')
  ) {
    tabItems.push({
      label: 'Address Details',
      value: 'Address Details',
    })
  }

  const [formData, setFormData] = useState({
    lead_source: 'QR',
    given_name: '',
    surname: '',
    email: '',
    national_id: '',
    phone: '',
    alternate_phone: '',
    date_of_birth: '',
    gender: 'Male',
    martial_status: 'Not Married',
    visa_type: 'Single',
    referral: '',
    father_name: '',
    mother_name: '',
    address: '',
    city: '',
    pin_code: '',
    state: '',
    country: 'India',
    country_code: 'IN',
    country_calling_code: '+91',
    alternate_country: 'India',
    alternate_country_code: 'IN',
    alternate_country_calling_code: '+91',
    application_email: '',
    application_password: '',
    vfs_password: '',
    visa_form_password: '',
    other_application_two_label: 'Email 2',
    other_application_two_email: '',
    other_application_two_password: '',
    other_application_three_label: 'Email 3',
    other_application_three_email: '',
    other_application_three_password: '',
  })

  useEffect(() => {
    axios
      .post<Record<string, string>>('/guest/select/lead-meta-options', {
        entity_name: 'countries',
        column_name: 'country',
        with_key: true,
        agency_id: agencyId,
        csrfToken: csrfToken,
      })
      .then((response) => {
        const countryList = Object.keys(response.data).map((key) => {
          return {
            value: key,
            label: response.data[key],
          }
        })

        setCountries(countryList)
      })
      .catch((error) => {
        console.error('Error fetching countries:', error)
      })
  }, [])

  useEffect(() => {
    if (leadStore.lead) {
      const lead = leadStore.lead
      setFormData((prevData) => ({
        ...prevData,
        lead_source: lead.lead_source || '',
        given_name: lead.given_name || '',
        surname: lead.surname || '',
        email: lead.email || '',
        phone: lead.phone || '',
        alternate_phone: lead.alternate_phone || '',
        date_of_birth: lead.date_of_birth || '',
        gender: lead.gender || 'Male',
        martial_status: lead.martial_status || 'Not Married',
        visa_type: lead.visa_type || 'Single',
        referral: lead.referral || '',
        father_name: lead.father_name || '',
        mother_name: lead.mother_name || '',
        address: lead.address || '',
        city: lead.city || '',
        pin_code: lead.pin_code || '',
        state: lead.state || '',
        country: lead.country || 'India',
        country_code: lead.country_code || 'IN',
        country_calling_code: lead.country_calling_code || '+91',
        alternate_country: lead.alternate_country || 'India',
        alternate_country_code: lead.alternate_country_code || 'IN',
        alternate_country_calling_code: lead.alternate_country_calling_code || '+91',
        application_email: lead.application_email || '',
        application_password: lead.application_password || '',
        vfs_password: lead.vfs_password || '',
        visa_form_password: lead.visa_form_password || '',
        other_application_two_label: lead.other_emails_and_passwords
          ? lead.other_emails_and_passwords[0]?.label
          : 'Email 2',
        other_application_two_email: lead.other_emails_and_passwords ? lead.other_emails_and_passwords[0]?.email : '',
        other_application_two_password: lead.other_emails_and_passwords
          ? lead.other_emails_and_passwords[0]?.password
          : '',
        other_application_three_label: lead.other_emails_and_passwords
          ? lead.other_emails_and_passwords[1]?.label
          : 'Email 3',
        other_application_three_email: lead.other_emails_and_passwords ? lead.other_emails_and_passwords[1]?.email : '',
        other_application_three_password: lead.other_emails_and_passwords
          ? lead.other_emails_and_passwords[1]?.password
          : '',
        national_id: lead.national_id || '',
      }))
    }
  }, [leadStore.lead])

  useEffect(() => {
    if (leadStore.visitor_phone !== null) {
      setFormData((prev) => {
        return {
          ...prev,
          phone: leadStore.visitor_phone || '',
          country: leadStore.visitor_country || 'India',
          country_code: leadStore.visitor_country_code || 'IN',
          country_calling_code: leadStore.visitor_country_calling_code || '+91',
        }
      })
    }
  }, [leadStore.visitor_phone])

  useEffect(() => {
    const prioritizeTab = () => {
      const errorKeys = Object.keys(errors)
      if (errorKeys.length > 0) {
        if (
          (fields.includes('given_name') && errors.given_name) ||
          (fields.includes('surname') && errors.surname) ||
          (fields.includes('email') && errors.email) ||
          (fields.includes('phone') && errors.phone) ||
          (fields.includes('national_id') && errors.national_id) ||
          (fields.includes('date_of_birth') && errors.date_of_birth) ||
          (fields.includes('martial_status') && errors.martial_status) ||
          (fields.includes('visa_type') && errors.visa_type) ||
          (fields.includes('referral') && errors.referral)
        ) {
          setTab('Personal Details')
        } else if (
          (fields.includes('father_name') && errors.father_name) ||
          (fields.includes('mother_name') && errors.mother_name)
        ) {
          setTab('Family Details')
        } else if (
          (fields.includes('address') && errors.address) ||
          (fields.includes('city') && errors.city) ||
          (fields.includes('pin_code') && errors.pin_code) ||
          (fields.includes('state') && errors.state) ||
          (fields.includes('country_code') && errors.country_code)
        ) {
          setTab('Address Details')
        }
      } else {
        setTab('Personal Details')
      }
    }

    prioritizeTab()
  }, [errors])

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const response = await axios.post(
        leadStore.lead_id
          ? `/leads/form/${leadStore.lead_id}/personal-details`
          : `/leads/form/${leadStore.lead_form_uuid}`,
        {
          ...formData,
          uuid: leadStore.lead_form_uuid,
          csrfToken: leadStore.csrfToken,
        }
      )

      const { action, payload } = response.data

      if (action === 'personal_details:create' || action === 'personal_details:update') {
        if (payload.lead) {
          setTab('Personal Details')
          dispatch(updateLead(payload.lead))
          dispatch(updateLeadId(payload.lead.id.toString()))
          dispatch(updateCurrentStep(leadStore.current_step + 1))
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setErrors(error.response.data.errors || {})
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {leadStore.is_mobile_view && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100vw', zIndex: 9999 }}>
          <HStack spacing={'16px'} justifyContent="flex-end">
            <Button
              style={{ borderRadius: 0, width: '100%' }}
              label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
              onClick={handleSubmit}
            ></Button>
          </HStack>
        </div>
      )}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ color: 'blueX' }} />
        <VStack spacing="12px" height="100%">
          <Tabs
            style={{
              width: '100%',
              borderBottomWidth: '2px',
              borderBottomStyle: 'solid',
              borderBottomColor: XelaColor.Gray12,
              borderRadius: '0px',
              flexWrap: leadStore.is_mobile_view ? 'wrap' : 'nowrap',
            }}
            items={tabItems}
            name="personal_details_edit_modal_tabs"
            defaultValue={tab}
            onChangeHandle={(value) => {
              setTab(value)
            }}
          ></Tabs>
          {tab === 'Personal Details' && (
            <VStack
              spacing="12px"
              style={{
                flex: '1 1 auto',
                padding: '2px',
                paddingRight: '10px',
                height: leadStore.is_mobile_view ? 'auto' : '0',
                marginBottom: leadStore.is_mobile_view ? '50px' : '0px',
                overflowY: 'auto',
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {fields.includes('given_name') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required
                      label={'Given Name'}
                      error={errors.given_name}
                      value={formData.given_name}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.given_name !== null}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          given_name: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('surname') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('surname')}
                      label={'Surname'}
                      error={errors.surname}
                      value={formData.surname}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.surname !== null}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          surname: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('email') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('email')}
                      label={'Email'}
                      error={errors.email}
                      value={formData.email}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.email !== null}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          email: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('phone') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XPhoneInput
                      required={requirements.includes('phone')}
                      disabled={leadStore.lead_id !== null || leadStore.visitor_phone !== null}
                      error={errors.phone}
                      dialCode={formData.country_calling_code}
                      countryCode={formData.country_code}
                      initialValue={formData.phone}
                      onChange={(number, dialCode, countryCode, country) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          phone: number,
                          country_calling_code: dialCode,
                          country_code: countryCode,
                          country: country,
                        }))
                      }}
                    ></XPhoneInput>
                  </HStack>
                )}
                {fields.includes('alternate_phone') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XPhoneInput
                      required={requirements.includes('alternate_phone')}
                      label={'Alternate Phone'}
                      error={errors.alternate_phone}
                      dialCode={formData.alternate_country_calling_code}
                      countryCode={formData.alternate_country_code}
                      initialValue={formData.alternate_phone}
                      onChange={(number, dialCode, countryCode, country) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          alternate_phone: number,
                          alternate_country_calling_code: dialCode,
                          alternate_country_code: countryCode,
                          alternate_country: country,
                        }))
                      }}
                    ></XPhoneInput>
                  </HStack>
                )}
                {fields.includes('date_of_birth') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XDatePicker
                      required={requirements.includes('date_of_birth')}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.date_of_birth !== null}
                      dateParser={(dateString) => {
                        return moment(dateString, 'DD/MM/YYYY').toDate()
                      }}
                      value={
                        formData.date_of_birth ? moment(formData.date_of_birth as string, 'YYYY-MM-DD').toDate() : null
                      }
                      width="100%"
                      placeholder="DD/MM/YYYY"
                      valueFormat={'DD/MM/YYYY'}
                      maxDate={new Date()}
                      label={'Date of Birth'}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            date_of_birth: moment(value).format('YYYY-MM-DD'),
                          }))
                        }
                      }}
                      error={errors.date_of_birth}
                    />
                  </HStack>
                )}
                {fields.includes('gender') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      required={requirements.includes('gender')}
                      searchable
                      label={'Gender'}
                      error={errors.gender}
                      value={formData.gender}
                      data={['Male', 'Female', 'Gender Fluid/Non Binary']}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            gender: value,
                          }))
                        }
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('martial_status') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      required={requirements.includes('martial_status')}
                      searchable
                      label={'Martial Status'}
                      error={errors.martial_status}
                      value={formData.martial_status}
                      data={['Married', 'Not Married']}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            martial_status: value,
                          }))
                        }
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('visa_type') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      required={requirements.includes('visa_type')}
                      searchable
                      label={'Visa Type'}
                      error={errors.visa_type}
                      value={formData.visa_type}
                      onInit={(callback) => {
                        axios
                          .post('/guest/select/lead-meta-options', {
                            entity_name: 'leads',
                            column_name: 'visa_type',
                            agency_id: agencyId,
                            csrfToken: csrfToken,
                          })
                          .then((response) => {
                            callback(response.data)
                          })
                          .catch((error) => {
                            console.log(error)
                            callback([])
                          })
                      }}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            visa_type: value,
                          }))
                        }
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('national_id') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XNationalInput
                      required={requirements.includes('national_id')}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.national_id !== null}
                      label={leadStore.lead_form_agency_id == '1111181' ? 'Civil ID' : 'National ID'}
                      error={errors.national_id}
                      value={formData.national_id}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          national_id: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('referral') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('referral')}
                      label={'Referral'}
                      error={errors.referral}
                      value={formData.referral}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.referral !== null}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          referral: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
              </div>
            </VStack>
          )}
          {tab === 'Family Details' && (
            <VStack
              spacing="12px"
              style={{
                flex: '1 1 auto',
                padding: '2px',
                paddingRight: '10px',
                height: leadStore.is_mobile_view ? 'auto' : '0',
                marginBottom: leadStore.is_mobile_view ? '50px' : '0px',
                overflowY: 'auto',
              }}
            >
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                {fields.includes('father_name') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('father_name')}
                      label={'Father Name'}
                      error={errors.father_name}
                      value={formData.father_name}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          father_name: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('mother_name') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('mother_name')}
                      label={'Mother Name'}
                      error={errors.mother_name}
                      value={formData.mother_name}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          mother_name: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                <div style={{ width: '100%' }}></div>
              </HStack>
            </VStack>
          )}
          {tab === 'Address Details' && (
            <VStack
              spacing="12px"
              style={{
                flex: '1 1 auto',
                padding: '2px',
                paddingRight: '10px',
                height: leadStore.is_mobile_view ? 'auto' : '0',
                marginBottom: leadStore.is_mobile_view ? '50px' : '0px',
                overflowY: 'auto',
              }}
            >
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                {fields.includes('address') && (
                  <HStack>
                    <XTextInput
                      required={requirements.includes('address')}
                      label={'Address'}
                      error={errors.address}
                      value={formData.address}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          address: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
              </HStack>
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                {fields.includes('city') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('city')}
                      label={'City'}
                      error={errors.city}
                      value={formData.city}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          city: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('pin_code') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('pin_code')}
                      label={'Pincode'}
                      error={errors.pin_code}
                      value={formData.pin_code}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          pin_code: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('state') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={requirements.includes('state')}
                      label={'State'}
                      error={errors.state}
                      value={formData.state}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          state: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
              </HStack>
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                {fields.includes('country_code') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      required={requirements.includes('country_code')}
                      searchable
                      label={'Country'}
                      error={errors.country}
                      value={formData.country_code}
                      data={countries}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            country_code: value,
                            country: countries.find((country) => country.value === value)?.label as string,
                          }))
                        }
                      }}
                    />
                  </HStack>
                )}
                <div style={{ width: '100%' }}></div>
                <div style={{ width: '100%' }}></div>
              </HStack>
            </VStack>
          )}
          {!leadStore.is_mobile_view && (
            <>
              <HStack>
                <Divider variant={'dotted'}></Divider>
              </HStack>
              <HStack spacing={'16px'} justifyContent="flex-end">
                <Button
                  label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
                  onClick={handleSubmit}
                ></Button>
              </HStack>
            </>
          )}
        </VStack>
      </div>
    </>
  )
}

export default LeadFormPersonalDetail
